import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell } from "recharts";
import { Users, Stethoscope, Buildings, TestTube, Warning, ChatCircleDots, SignOut } from "@phosphor-icons/react";

const COLORS = ["#114232", "#D26038", "#4A6B82", "#8B9A8F", "#B8956B"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState({ appointments: [], lab_reports: [], sos_events: [], recent_users: [] });
  const [whatsapp, setWhatsapp] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/recent-activity").then((r) => setActivity(r.data));
    api.get("/admin/whatsapp").then((r) => setWhatsapp(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="p-12 text-charcoal">Loading dashboard…</div>;

  const kpis = [
    { label: "Users", value: stats.users, icon: Users },
    { label: "Patients", value: stats.patients, icon: Users },
    { label: "Doctors", value: stats.doctors, icon: Stethoscope },
    { label: "Hospitals", value: stats.hospitals, icon: Buildings },
    { label: "Appointments", value: stats.appointments, icon: Stethoscope },
    { label: "Paid appts", value: stats.appointments_paid, icon: Stethoscope },
    { label: "Lab reports", value: stats.lab_reports, icon: TestTube },
    { label: "Lab pending", value: stats.lab_pending, icon: TestTube },
    { label: "SOS events", value: stats.sos_events, icon: Warning },
    { label: "AI messages", value: stats.ai_messages, icon: ChatCircleDots },
  ];

  const chartData = [
    { name: "Patients", v: stats.patients },
    { name: "Doctors", v: stats.doctors },
    { name: "Hospitals", v: stats.hospitals },
    { name: "Apts", v: stats.appointments },
    { name: "Reports", v: stats.lab_reports },
    { name: "SOS", v: stats.sos_events },
  ];

  const pieData = [
    { name: "Reviewed", value: Math.max(0, stats.lab_reports - stats.lab_pending) },
    { name: "Pending", value: stats.lab_pending },
  ];

  return (
    <div className="min-h-screen bg-bone flex">
      {/* Admin sidebar */}
      <aside className="hidden md:flex w-60 flex-col bg-charcoal text-bone">
        <div className="p-6 border-b border-white/10">
          <div className="font-clinical text-xl">Gutvibe</div>
          <div className="overline text-terracotta text-[10px] mt-1">Admin Console</div>
        </div>
        <nav className="p-3 space-y-1 flex-1">
          <Link to="/admin" className="block px-4 py-2 rounded-md bg-white/10 text-sm">Overview</Link>
          <Link to="/doctor" className="block px-4 py-2 rounded-md hover:bg-white/10 text-sm text-bone/70">Doctor portal</Link>
          <Link to="/hospital" className="block px-4 py-2 rounded-md hover:bg-white/10 text-sm text-bone/70">Hospital portal</Link>
          <Link to="/lab" className="block px-4 py-2 rounded-md hover:bg-white/10 text-sm text-bone/70">Lab review</Link>
          <Link to="/app" className="block px-4 py-2 rounded-md hover:bg-white/10 text-sm text-bone/70">Patient app</Link>
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-sm mb-2">{user?.name}</div>
          <Button size="sm" variant="secondary" onClick={() => { logout(); nav("/"); }} className="w-full bg-white/10 hover:bg-white/20 text-white border-0" data-testid="admin-logout">
            <SignOut size={14} className="mr-1" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-10 overflow-x-hidden">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="overline text-terracotta">Real-time operations</div>
            <h1 className="font-clinical text-4xl text-charcoal mt-1">Control Centre</h1>
          </div>
          <Badge className="bg-green-800 text-white">Live</Badge>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          {kpis.map((k) => (
            <Card key={k.label} className="border-black/5 bg-white rounded-md" data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g,'-')}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-charcoal/50">
                  <k.icon size={16} weight="duotone" />
                  <div className="overline text-[10px]">{k.label}</div>
                </div>
                <div className="font-clinical text-2xl text-charcoal mt-1">{k.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Card className="md:col-span-2 border-black/5 bg-white rounded-md">
            <CardContent className="p-5">
              <div className="overline text-charcoal/50 mb-3">Platform footprint</div>
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData}>
                    <XAxis dataKey="name" fontSize={11} stroke="#1A211D" />
                    <YAxis fontSize={11} stroke="#1A211D" />
                    <Tooltip />
                    <Bar dataKey="v" fill="#114232" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          <Card className="border-black/5 bg-white rounded-md">
            <CardContent className="p-5">
              <div className="overline text-charcoal/50 mb-3">Lab review status</div>
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80}>
                      {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-4 text-xs mt-2">
                {pieData.map((p, i) => (
                  <div key={p.name} className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full" style={{ background: COLORS[i] }} />
                    {p.name}: {p.value}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="border-black/5 bg-white rounded-md">
            <CardContent className="p-5">
              <div className="overline text-charcoal/50 mb-3">Recent appointments</div>
              <div className="space-y-2">
                {activity.appointments.length === 0 && <div className="text-sm text-charcoal/50">No activity</div>}
                {activity.appointments.map((a) => (
                  <div key={a.id} className="text-sm border-b border-black/5 pb-2 last:border-0">
                    <div className="font-clinical text-charcoal">{a.patient_name} → {a.doctor_name}</div>
                    <div className="text-xs text-charcoal/60">{a.specialty} · {new Date(a.slot_iso).toLocaleString("en-IN")} · <Badge variant="outline" className="text-[9px]">{a.payment_status}</Badge></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="border-black/5 bg-white rounded-md">
            <CardContent className="p-5">
              <div className="overline text-charcoal/50 mb-3">Recent SOS events</div>
              <div className="space-y-2">
                {activity.sos_events.length === 0 && <div className="text-sm text-charcoal/50">No SOS events</div>}
                {activity.sos_events.map((s) => (
                  <div key={s.id} className="text-sm border-b border-black/5 pb-2 last:border-0">
                    <div className="font-clinical text-charcoal">{s.user_name}</div>
                    <div className="text-xs text-charcoal/60">{new Date(s.created_at).toLocaleString("en-IN")} · {s.contacts_notified.length} contacts notified</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="border-black/5 bg-white rounded-md md:col-span-2">
            <CardContent className="p-5">
              <div className="overline text-charcoal/50 mb-3">Recent users</div>
              <div className="space-y-2">
                {activity.recent_users.map((u) => (
                  <div key={u.id} className="text-sm border-b border-black/5 pb-2 last:border-0 flex items-center justify-between">
                    <div>
                      <div className="font-clinical text-charcoal">{u.name} <span className="text-xs text-charcoal/50">· {u.email}</span></div>
                      <div className="text-xs text-charcoal/60">Joined {new Date(u.created_at).toLocaleString("en-IN")}</div>
                    </div>
                    <Badge className="bg-ayurveda text-bone capitalize text-[10px]">{u.role}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="border-black/5 bg-white rounded-md md:col-span-2">
            <CardContent className="p-5">
              <div className="overline text-charcoal/50 mb-3 flex items-center justify-between">
                <span>WhatsApp queue (simulated)</span>
                <Badge className="bg-green-800 text-white text-[10px]">{whatsapp.length} messages</Badge>
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {whatsapp.length === 0 && <div className="text-sm text-charcoal/50">No messages sent yet</div>}
                {whatsapp.map((m) => (
                  <div key={m.id} className="text-sm border-b border-black/5 pb-2 last:border-0" data-testid={`wa-${m.id}`}>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[9px]">{m.template}</Badge>
                      <span className="text-xs text-charcoal/60">→ {m.phone || "no phone"}</span>
                      <span className="text-[10px] text-charcoal/40 ml-auto">{new Date(m.created_at).toLocaleTimeString("en-IN")}</span>
                    </div>
                    <div className="text-xs text-charcoal/70 mt-1">{m.body}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
